import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Snackbar,
  Paper,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Description as DocxIcon,
  PhotoSizeSelectSmall as SmallImageIcon,
} from '@mui/icons-material';
import { extractApi } from '../../services/api';

const BRAND_ORANGE = '#ff7c38';

// Slack only shows a pasted image inline (rather than as a file attachment) if
// it's within these limits — see
// https://slack.com/help/articles/201330736-Add-files-to-Slack
const SLACK_MAX_SIDE_PX = 25000;
const SLACK_MAX_TOTAL_PX = 45000000;

export default function Extract() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const previewRef = useRef(null);

  const handleFetch = async (e) => {
    e.preventDefault();
    setError('');
    setPreview(null);
    setLoading(true);
    try {
      const res = await extractApi.preview(url.trim());
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not fetch that comment');
    } finally {
      setLoading(false);
    }
  };

  const COPIED_MSG = 'Copied — paste into Gmail, Docs, Notion, or Slack Canvas to keep formatting and images. '
    + "(Slack's regular message box doesn't support rich paste.)";
  const COPIED_SMALL_MSG = 'Copied with Slack-safe image sizes — oversized images were scaled down so they preview '
    + 'inline when pasted into Slack instead of appearing as attachments.';

  const legacyCopyNode = (node) => {
    node.style.position = 'fixed';
    node.style.top = '-9999px';
    document.body.appendChild(node);
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand('copy');
    selection.removeAllRanges();
    document.body.removeChild(node);
  };

  const legacyCopyFallback = () => {
    if (!previewRef.current) return;
    legacyCopyNode(previewRef.current);
    setSnackbarMsg(COPIED_MSG);
  };

  const handleCopy = () => {
    if (!preview) return;
    try {
      const item = new ClipboardItem({
        'text/html': new Blob([preview.html], { type: 'text/html' }),
        'text/plain': new Blob([preview.plain_text], { type: 'text/plain' }),
      });
      navigator.clipboard.write([item]).then(() => setSnackbarMsg(COPIED_MSG)).catch(legacyCopyFallback);
    } catch (err) {
      // Browsers without ClipboardItem support: select the preview node instead.
      legacyCopyFallback();
    }
  };

  // Downscales a single data:image/... URI so it fits Slack's inline-preview
  // pixel limits. Returns the original src untouched if it's already within
  // both limits, since re-encoding would just lose quality for nothing.
  const resizeImageForSlack = (src) => new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const { naturalWidth: w, naturalHeight: h } = img;
      const scale = Math.min(
        1,
        SLACK_MAX_SIDE_PX / Math.max(w, h),
        Math.sqrt(SLACK_MAX_TOTAL_PX / (w * h)),
      );
      if (scale >= 1) {
        resolve(src);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const mime = src.startsWith('data:image/png') ? 'image/png' : 'image/jpeg';
      resolve(canvas.toDataURL(mime, 0.9));
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });

  // Builds a copy of preview.html with any over-limit images replaced by
  // scaled-down versions, so the result can be pasted into Slack inline.
  const buildSlackSafeNode = async () => {
    const container = document.createElement('div');
    container.innerHTML = preview.html;
    const imgs = Array.from(container.querySelectorAll('img'));
    await Promise.all(imgs.map(async (imgEl) => {
      const src = imgEl.getAttribute('src');
      if (src && src.startsWith('data:image/')) {
        imgEl.setAttribute('src', await resizeImageForSlack(src));
      }
    }));
    return container;
  };

  const handleCopySmall = async () => {
    if (!preview) return;
    setResizing(true);
    try {
      const node = await buildSlackSafeNode();
      const item = new ClipboardItem({
        'text/html': new Blob([node.innerHTML], { type: 'text/html' }),
        'text/plain': new Blob([preview.plain_text], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      setSnackbarMsg(COPIED_SMALL_MSG);
    } catch (err) {
      try {
        const node = await buildSlackSafeNode();
        legacyCopyNode(node);
        setSnackbarMsg(COPIED_SMALL_MSG);
      } catch (fallbackErr) {
        setError('Could not copy a Slack-safe version — try Copy instead.');
      }
    } finally {
      setResizing(false);
    }
  };

  const handleDownloadDocx = async () => {
    setDownloading(true);
    setError('');
    try {
      const res = await extractApi.downloadDocx(url.trim());
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : 'gitlab-comment.docx';

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError('Could not generate the Word document');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Extract
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Paste a GitLab issue or merge request comment link to pull out its text and images —
        ready to copy/paste into Gmail, Docs, Notion, or Slack Canvas, or download as a Word document.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box component="form" onSubmit={handleFetch} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              label="GitLab comment URL"
              placeholder="https://gitlab.example.com/group/project/-/issues/123#note_456789"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              required
              sx={{ flex: 1, minWidth: 280 }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !url.trim()}
              sx={{ px: 3 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Fetch'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {preview && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {preview.author}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {preview.created_at}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon fontSize="small" />}
                  onClick={handleCopy}
                  sx={{ borderColor: BRAND_ORANGE, color: BRAND_ORANGE }}
                >
                  Copy
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={resizing ? <CircularProgress size={16} /> : <SmallImageIcon fontSize="small" />}
                  onClick={handleCopySmall}
                  disabled={resizing}
                  sx={{ borderColor: BRAND_ORANGE, color: BRAND_ORANGE }}
                >
                  Copy Using Small Images
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={downloading ? <CircularProgress size={16} /> : <DocxIcon fontSize="small" />}
                  onClick={handleDownloadDocx}
                  disabled={downloading}
                >
                  Download .docx
                </Button>
              </Box>
            </Box>

            <Paper
              variant="outlined"
              sx={{ p: 2, backgroundColor: '#fff', maxHeight: 600, overflow: 'auto' }}
            >
              <div ref={previewRef} dangerouslySetInnerHTML={{ __html: preview.html }} />
            </Paper>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={4000}
        onClose={() => setSnackbarMsg('')}
        message={snackbarMsg}
      />
    </Box>
  );
}
